import React, {useCallback, useEffect, useRef, useState} from 'react';
import {Link, useNavigate, useParams} from 'react-router-dom';
import Layout from '../components/Layout';
import {api} from '../api';
import * as pdfjsLib from 'pdfjs-dist';

pdfjsLib.GlobalWorkerOptions.workerSrc = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjsLib.version}/pdf.worker.min.mjs`;

const SIGNER_COLORS = ['#059669','#2563eb','#d97706','#dc2626','#7c3aed','#0891b2'];
const DOC_FIELD_COLOR = '#6b7280';
const DOC_FIELD_TYPES = ['DOC_DATE','DOC_DATE_DAY','DOC_DATE_MONTH','DOC_DATE_YEAR_2','DOC_DATE_YEAR_4','DOC_CONTRACT_NUMBER','DOC_ENTITY_NAME'];
const DOC_FIELD_LABELS = {'DOC_DATE':'📅 Date','DOC_DATE_DAY':'Day','DOC_DATE_MONTH':'Month','DOC_DATE_YEAR_2':'Year 2','DOC_DATE_YEAR_4':'Year 4','DOC_CONTRACT_NUMBER':'# Contract #','DOC_ENTITY_NAME':'🏢 Entity'};
const DOC_FIELD_WIDTHS = {'DOC_DATE':120,'DOC_DATE_DAY':40,'DOC_DATE_MONTH':80,'DOC_DATE_YEAR_2':40,'DOC_DATE_YEAR_4':60,'DOC_CONTRACT_NUMBER':140,'DOC_ENTITY_NAME':180};
const DOC_FIELD_PLACEHOLDERS = {'DOC_DATE':'01/01/2025','DOC_DATE_DAY':'DD','DOC_DATE_MONTH':'Month','DOC_DATE_YEAR_2':'YY','DOC_DATE_YEAR_4':'YYYY','DOC_CONTRACT_NUMBER':'Contract #','DOC_ENTITY_NAME':'Entity Name'};
const isDocField = (type) => DOC_FIELD_TYPES.includes(type);

function signerColor(signers, signerId) {
  const idx = signers?.findIndex(s => s.id === signerId) ?? 0;
  return SIGNER_COLORS[idx % SIGNER_COLORS.length];
}

export default function DocumentDetail() {
  const {id} = useParams();
  const navigate = useNavigate();
  const [doc, setDoc] = useState(null);
  const [editingTitle, setEditingTitle] = useState(null);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [pages, setPages] = useState([]);
  const [showAddSigner, setShowAddSigner] = useState(false);
  const [signerEmail, setSignerEmail] = useState('');
  const [signerName, setSignerName] = useState('');
  const [placingField, setPlacingField] = useState(null); // {signerId, fieldType}
  const [draggingField, setDraggingField] = useState(null); // existing field being moved
  const [dragPos, setDragPos] = useState(null); // {x, y} current mouse position relative to page
  const [resizingField, setResizingField] = useState(null); // field being resized
  const [selectedField, setSelectedField] = useState(null); // field with settings open
  const [docFieldsOpen, setDocFieldsOpen] = useState(true);
  const [signersOpen, setSignersOpen] = useState(true);
  const [expandedSigners, setExpandedSigners] = useState({});
  const [dragOffset, setDragOffset] = useState({x: 0, y: 0});
  const [toast, setToast] = useState(null);
  const pageRefs = useRef({});
  const pdfScales = useRef({});
  const [scalesReady, setScalesReady] = useState(0);

  const load = useCallback(() => api(`/esign/documents/${id}`).then(r => r.ok ? r.json() : null).then(setDoc).catch(() => {}), [id]);
  useEffect(() => { load(); }, [load]);

  // Load PDF URL
  useEffect(() => {
    if (!doc) return;
    api(`/esign/documents/${id}/view-url`).then(r => r.ok ? r.json() : null).then(data => {
      if (data?.url) setPdfUrl(data.url);
    });
  }, [id, doc]);

  // Render PDF pages
  useEffect(() => {
    if (!pdfUrl) return;
    let cancelled = false;
    pdfjsLib.getDocument(pdfUrl).promise.then(pdf => {
      if (cancelled) return;
      const pagePromises = [];
      for (let i = 1; i <= pdf.numPages; i++) {
        pagePromises.push(pdf.getPage(i).then(page => {
          const viewport = page.getViewport({scale: 1});
          return {pageNum: i, width: viewport.width, height: viewport.height, page};
        }));
      }
      return Promise.all(pagePromises);
    }).then(pgs => {
      if (!cancelled && pgs) setPages(pgs);
    });
    return () => { cancelled = true; };
  }, [pdfUrl]);

  // Render each page to canvas
  useEffect(() => {
    let rendered = 0;
    pages.forEach(({pageNum, page, width, height}) => {
      const canvas = pageRefs.current[pageNum];
      if (!canvas || canvas.dataset.rendered === 'true') return;
      const containerWidth = canvas.parentElement.clientWidth;
      const scale = containerWidth / width;
      pdfScales.current[pageNum] = {scale, pdfWidth: width, pdfHeight: height};
      const viewport = page.getViewport({scale});
      canvas.width = viewport.width;
      canvas.height = viewport.height;
      canvas.dataset.rendered = 'true';
      page.render({canvasContext: canvas.getContext('2d'), viewport});
      rendered++;
    });
    if (rendered > 0) setScalesReady(prev => prev + 1);
  }, [pages]);

  // Cancel drag if mouse released outside page areas
  useEffect(() => {
    if (!draggingField) return;
    const handleGlobalMouseUp = () => { setDraggingField(null); setDragPos(null); };
    window.addEventListener('mouseup', handleGlobalMouseUp);
    return () => window.removeEventListener('mouseup', handleGlobalMouseUp);
  }, [draggingField]);

  const handleAddSigner = async (e) => {
    e.preventDefault();
    if (!signerEmail.trim() || !signerName.trim()) return;
    await api(`/esign/documents/${id}/signers`, {
      method: 'POST', body: JSON.stringify({email: signerEmail.trim(), name: signerName.trim()}),
    });
    setShowAddSigner(false);
    setSignerEmail('');
    setSignerName('');
    load();
  };

  const handleRemoveSigner = async (signerId) => {
    await api(`/esign/documents/${id}/signers/${signerId}`, {method: 'DELETE'});
    load();
  };

  const handleResendEmail = async (signerId) => {
    const res = await api(`/esign/documents/${id}/signers/${signerId}/resend`, {method: 'POST'});
    setToast(res.ok ? {type: 'success', message: 'Signing email resent successfully'} : {type: 'error', message: 'Failed to resend email'});
    setTimeout(() => setToast(null), 3000);
  };

  const handleDeleteField = async (fieldId) => {
    await api(`/esign/documents/${id}/fields/${fieldId}`, {method: 'DELETE'});
    setSelectedField(null);
    load();
  };

  const handleUpdateFieldStyle = async (field, updates) => {
    await api(`/esign/documents/${id}/fields/${field.id}`, {
      method: 'PUT',
      body: JSON.stringify({
        signerId: field.signerId, fieldType: field.fieldType, pageNumber: field.pageNumber,
        xPosition: field.xPosition, yPosition: field.yPosition, width: field.width, height: field.height,
        fontSize: updates.fontSize ?? field.fontSize, fontFamily: updates.fontFamily ?? field.fontFamily,
        textAlign: updates.textAlign ?? field.textAlign, label: updates.label !== undefined ? updates.label : field.label,
        value: updates.value !== undefined ? updates.value : field.value,
      }),
    });
    load();
  };

  const [showSendConfirm, setShowSendConfirm] = useState(false);

  const handleSend = async () => {
    const res = await api(`/esign/documents/${id}/send`, {method: 'POST'});
    setShowSendConfirm(false);
    if (res.ok) load();
  };

  const handleTitleSave = async (e) => {
    e.preventDefault();
    if (!editingTitle?.trim() || editingTitle.trim() === doc.title) { setEditingTitle(null); return; }
    const res = await api(`/esign/documents/${id}`, { method: 'PUT', body: JSON.stringify({ title: editingTitle.trim() }) });
    if (res.ok) load();
    setEditingTitle(null);
  };

  const handleDelete = async () => {
    await api(`/esign/documents/${id}`, {method: 'DELETE'});
    navigate('/');
  };

  // Click on page to place a new field
  const handlePageClick = async (e, pageNum) => {
    if (fieldClickedRef.current) { fieldClickedRef.current = false; return; }
    if (!placingField) { setSelectedField(null); return; }
    const canvas = pageRefs.current[pageNum];
    if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    const scaleInfo = pdfScales.current[pageNum];
    if (!scaleInfo) return;

    const clickX = e.clientX - rect.left;
    const clickY = e.clientY - rect.top;
    const pdfX = clickX / scaleInfo.scale;
    const pdfY = clickY / scaleInfo.scale;

    const fieldWidth = {'DATE': 120, 'NAME': 160, 'EMAIL': 180, 'STREET_ADDRESS_1': 220, 'STREET_ADDRESS_2': 220, 'CITY': 120, 'STATE': 80, 'ZIP_CODE': 80, 'COUNTRY': 140, 'COUNTRY_ABBR': 50, 'FIRST_NAME': 120, 'LAST_NAME': 120, 'DATE_DAY': 40, 'DATE_MONTH': 80, 'DATE_YEAR_4': 60, 'DATE_YEAR_2': 40, 'FREE_TEXT': 160, ...DOC_FIELD_WIDTHS}[placingField.fieldType] || 200;
    const fieldHeight = {'DATE': 30, 'NAME': 30, 'EMAIL': 30, 'FIRST_NAME': 30, 'LAST_NAME': 30, 'DATE_DAY': 30, 'DATE_MONTH': 30, 'DATE_YEAR_4': 30, 'DATE_YEAR_2': 30, 'FREE_TEXT': 30}[placingField.fieldType] || 30;

    await api(`/esign/documents/${id}/fields`, {
      method: 'POST',
      body: JSON.stringify({
        signerId: placingField.signerId,
        fieldType: placingField.fieldType,
        pageNumber: pageNum,
        xPosition: Math.max(0, pdfX - fieldWidth / 2),
        yPosition: Math.max(0, pdfY - fieldHeight / 2),
        width: fieldWidth,
        height: fieldHeight,
        label: placingField.label || undefined,
      }),
    });
    setPlacingField(null);
    load();
  };

  // Track whether a field was just clicked so handlePageClick can skip clearing selection
  const fieldClickedRef = useRef(false);
  const fieldOverlayRefs = useRef({});

  // Drag existing field
  const handleFieldMouseDown = (e, field) => {
    if (doc.status !== 'DRAFT') return;
    e.stopPropagation();
    fieldClickedRef.current = true;
    setSelectedField(field);
    const el = e.currentTarget;
    const rect = el.getBoundingClientRect();
    const offsetX = e.clientX - rect.left;
    const offsetY = e.clientY - rect.top;
    const startX = e.clientX;
    const startY = e.clientY;
    let started = false;

    const onMove = (ev) => {
      if (!started && Math.abs(ev.clientX - startX) + Math.abs(ev.clientY - startY) > 5) {
        started = true;
        setDragOffset({x: offsetX, y: offsetY});
        setDraggingField(field);
      }
    };
    const onUp = () => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
    };
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
  };

  const handleResizeMouseDown = (e, field, pageNum) => {
    if (doc.status !== 'DRAFT') return;
    e.stopPropagation();
    e.preventDefault();
    const canvas = pageRefs.current[pageNum];
    if (!canvas) return;
    const scaleInfo = pdfScales.current[pageNum];
    if (!scaleInfo) return;

    const startX = e.clientX;
    const startY = e.clientY;
    const startW = field.width;
    const startH = field.height;

    const onMove = (ev) => {
      const dx = (ev.clientX - startX) / scaleInfo.scale;
      const dy = (ev.clientY - startY) / scaleInfo.scale;
      setResizingField({...field, width: Math.max(40, startW + dx), height: Math.max(20, startH + dy)});
    };
    const onUp = async (ev) => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
      const dx = (ev.clientX - startX) / scaleInfo.scale;
      const dy = (ev.clientY - startY) / scaleInfo.scale;
      const newW = Math.max(40, startW + dx);
      const newH = Math.max(20, startH + dy);
      setResizingField(null);
      await api(`/esign/documents/${id}/fields/${field.id}`, {
        method: 'PUT',
        body: JSON.stringify({
          signerId: field.signerId, fieldType: field.fieldType, pageNumber: pageNum,
          xPosition: field.xPosition, yPosition: field.yPosition, width: newW, height: newH,
          label: field.label,
        }),
      });
      load();
    };
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
  };

  const handlePageMouseUp = async (e, pageNum) => {
    if (!draggingField) return;
    e.stopPropagation();
    const canvas = pageRefs.current[pageNum];
    if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    const scaleInfo = pdfScales.current[pageNum];
    if (!scaleInfo) return;

    const dropX = e.clientX - rect.left - dragOffset.x;
    const dropY = e.clientY - rect.top - dragOffset.y;
    const pdfX = Math.max(0, dropX / scaleInfo.scale);
    const pdfY = Math.max(0, dropY / scaleInfo.scale);

    await api(`/esign/documents/${id}/fields/${draggingField.id}`, {
      method: 'PUT',
      body: JSON.stringify({
        signerId: draggingField.signerId,
        fieldType: draggingField.fieldType,
        pageNumber: pageNum,
        xPosition: pdfX,
        yPosition: pdfY,
        width: draggingField.width,
        height: draggingField.height,
        fontSize: draggingField.fontSize,
        fontFamily: draggingField.fontFamily,
        textAlign: draggingField.textAlign,
        label: draggingField.label,
      }),
    });
    setDraggingField(null);
    setDragPos(null);
    load();
  };

  if (!doc) return <Layout><div className="text-center py-12 text-gray-500">Loading...</div></Layout>;

  const isDraft = doc.status === 'DRAFT';

  return (
    <Layout>
      {toast && (
        <div className={`fixed top-4 right-4 z-50 px-4 py-3 rounded-lg shadow-lg text-sm font-medium text-white transition-opacity ${
          toast.type === 'success' ? 'bg-emerald-600' : 'bg-red-600'
        }`}>
          {toast.message}
        </div>
      )}
      <div className="mb-6">
        <Link to="/" className="text-emerald-600 hover:text-emerald-800 text-sm">← Back to Documents</Link>
      </div>

      {/* Header */}
      <div className="bg-white rounded-lg shadow p-6 mb-6">
        <div className="flex justify-between items-start">
          <div>
            {editingTitle !== null ? (
              <form onSubmit={handleTitleSave} className="flex items-center gap-2">
                <input type="text" value={editingTitle} onChange={e => setEditingTitle(e.target.value)} autoFocus
                  onBlur={handleTitleSave}
                  className="text-2xl font-bold text-gray-900 border-b-2 border-emerald-500 outline-none bg-transparent" />
              </form>
            ) : (
              <h2 className="text-2xl font-bold text-gray-900 cursor-pointer hover:text-emerald-700" onClick={() => isDraft && setEditingTitle(doc.title)}>
                {doc.title}{isDraft && <span className="text-gray-300 text-sm ml-2">✎</span>}
              </h2>
            )}
            <p className="text-gray-500 text-sm mt-1">{doc.fileName} • {pages.length || doc.pageCount || '?'} pages</p>
            <span className={`inline-flex px-2 py-1 rounded text-xs font-medium mt-2 ${
              doc.status === 'COMPLETED' ? 'bg-green-50 text-green-700' :
              doc.status === 'SENT' ? 'bg-blue-50 text-blue-700' :
              'bg-gray-100 text-gray-600'
            }`}>{doc.status}</span>
          </div>
          <div className="flex gap-2">
            {pdfUrl && !isDraft && (
              <a href={pdfUrl} target="_blank" rel="noopener noreferrer"
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 text-sm">
                Download
              </a>
            )}
            {isDraft && (
              <button onClick={() => setShowSendConfirm(true)} disabled={!doc.signers?.length || !doc.fields?.length}
                className="px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 disabled:opacity-50 text-sm">
                Send for Signing
              </button>
            )}
            {isDraft && (
              <button onClick={handleDelete} className="px-4 py-2 border border-red-300 text-red-600 rounded-lg hover:bg-red-50 text-sm">Delete</button>
            )}
          </div>
        </div>
      </div>

      <div className="flex flex-col lg:flex-row gap-6">
        {/* Sidebar: Signers + Field Palette */}
        <div className="lg:w-72 flex-shrink-0 space-y-4 lg:sticky lg:top-4 lg:self-start lg:max-h-[calc(100vh-2rem)] lg:overflow-y-auto">
          {/* Document Fields */}
          <div className="bg-white rounded-lg shadow">
            <button onClick={() => setDocFieldsOpen(!docFieldsOpen)} className="w-full p-4 flex justify-between items-center">
              <h3 className="font-semibold text-gray-900 text-sm">Document Fields</h3>
              <span className={`text-gray-400 text-xs transition-transform ${docFieldsOpen ? 'rotate-180' : ''}`}>▾</span>
            </button>
            {docFieldsOpen && (
              <div>
                {isDraft && (
                  <div className="px-4 pb-3">
                    <p className="text-xs text-gray-500 mb-2">Fields filled by the sender, not signers.</p>
                    <div className="flex gap-1 flex-wrap">
                      {DOC_FIELD_TYPES.map(type => {
                        const active = placingField?.fieldType === type;
                        return (
                          <button key={type} onClick={() => setPlacingField(active ? null : {signerId: null, fieldType: type})}
                            className={`px-2 py-1 rounded text-xs border ${active ? 'bg-gray-600 text-white border-gray-600' : 'border-gray-300 text-gray-700 hover:bg-gray-50'}`}>
                            {DOC_FIELD_LABELS[type]}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}
                {doc.fields?.filter(f => isDocField(f.fieldType)).length > 0 && (
                  <div className="border-t divide-y">
                    {doc.fields.filter(f => isDocField(f.fieldType)).map(f => {
                      const isSelected = selectedField?.id === f.id;
                      const showSettings = isDraft && isSelected && !['SIGNATURE','INITIALS'].includes(f.fieldType);
                      return (
                        <div key={f.id}>
                          <div className={`px-4 py-2 cursor-pointer ${isSelected ? 'bg-emerald-50' : 'hover:bg-gray-50'}`} onClick={() => {
                            const next = isSelected ? null : f;
                            setSelectedField(next);
                            if (next && fieldOverlayRefs.current[f.id]) fieldOverlayRefs.current[f.id].scrollIntoView({behavior: 'smooth', block: 'center'});
                          }}>
                            <div className="flex items-center justify-between">
                              <div className="text-xs">
                                <span className="font-medium">{f.fieldType}</span>
                                <span className="text-gray-400 ml-1">p{f.pageNumber}</span>
                              </div>
                              {isDraft && (
                                <button onClick={(e) => { e.stopPropagation(); handleDeleteField(f.id); }} className="text-red-400 hover:text-red-600 text-xs">✕</button>
                              )}
                            </div>
                          </div>
                          {showSettings && (
                            <div className="px-4 pb-2 bg-emerald-50 flex items-center gap-2 flex-wrap">
                              <select value={selectedField.fontFamily || 'Helvetica'}
                                onChange={e => { handleUpdateFieldStyle(selectedField, {fontFamily: e.target.value}); setSelectedField({...selectedField, fontFamily: e.target.value}); }}
                                className="text-xs border border-gray-300 rounded px-1.5 py-1">
                                <option value="Helvetica">Helvetica</option><option value="Times New Roman">Times New Roman</option><option value="Courier">Courier</option>
                              </select>
                              <select value={selectedField.fontSize || 14}
                                onChange={e => { const v = parseInt(e.target.value); handleUpdateFieldStyle(selectedField, {fontSize: v}); setSelectedField({...selectedField, fontSize: v}); }}
                                className="text-xs border border-gray-300 rounded px-1.5 py-1">
                                {[8,9,10,11,12,14,16,18,20,24,28,32,36].map(s => <option key={s} value={s}>{s}pt</option>)}
                              </select>
                              <div className="flex border border-gray-300 rounded overflow-hidden">
                                {['LEFT','CENTER','RIGHT'].map(a => (
                                  <button key={a} onClick={() => { handleUpdateFieldStyle(selectedField, {textAlign: a}); setSelectedField({...selectedField, textAlign: a}); }}
                                    className={`px-1.5 py-1 text-xs ${(selectedField.textAlign || 'LEFT') === a ? 'bg-emerald-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}>
                                    {a === 'LEFT' ? '⫷' : a === 'CENTER' ? '☰' : '⫸'}
                                  </button>
                                ))}
                              </div>
                              {isDocField(selectedField.fieldType) && (
                                <input type="text" value={selectedField.value || ''} placeholder={DOC_FIELD_PLACEHOLDERS[selectedField.fieldType] || 'Value'}
                                  onChange={e => setSelectedField({...selectedField, value: e.target.value})}
                                  onBlur={e => handleUpdateFieldStyle(selectedField, {value: e.target.value})}
                                  className="text-xs border border-gray-300 rounded px-1.5 py-1 w-full mt-1" />
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Signers */}
          <div className="bg-white rounded-lg shadow">
            <div className="flex items-center justify-between">
              <button onClick={() => setSignersOpen(!signersOpen)} className="flex-1 p-4 flex justify-between items-center">
                <h3 className="font-semibold text-gray-900 text-sm">Signers</h3>
                <span className={`text-gray-400 text-xs transition-transform ${signersOpen ? 'rotate-180' : ''}`}>▾</span>
              </button>
              {isDraft && (
                <button onClick={(e) => { e.stopPropagation(); setSignersOpen(true); setShowAddSigner(true); }} className="text-emerald-600 hover:text-emerald-800 text-sm pr-4">+ Add</button>
              )}
            </div>
            {signersOpen && (
              <div>
                {(!doc.signers || doc.signers.length === 0) && (
                  <p className="text-center py-4 text-gray-400 text-xs">No signers yet</p>
                )}
                {doc.signers?.map((s, i) => {
                  const color = SIGNER_COLORS[i % SIGNER_COLORS.length];
                  const isExpanded = expandedSigners[s.id];
                  const signerFields = doc.fields?.filter(f => f.signerId === s.id) || [];
                  return (
                    <div key={s.id} className="border-t">
                      <div className="px-4 py-2 flex items-center justify-between">
                        <button onClick={() => setExpandedSigners(prev => ({...prev, [s.id]: !prev[s.id]}))} className="flex items-center gap-2 flex-1 min-w-0 text-left">
                          <span className={`text-gray-400 text-xs transition-transform ${isExpanded ? 'rotate-180' : ''}`}>▾</span>
                          <div className="w-3 h-3 rounded-full flex-shrink-0" style={{backgroundColor: color}} />
                          <span className="text-sm font-medium text-gray-900 truncate">{s.name}</span>
                          <span className={`inline-flex px-1.5 py-0.5 rounded text-xs flex-shrink-0 ${s.status === 'SIGNED' ? 'bg-green-50 text-green-700' : 'bg-yellow-50 text-yellow-700'}`}>{s.status}</span>
                          {signerFields.length > 0 && <span className="text-xs text-gray-400 flex-shrink-0">({signerFields.length})</span>}
                        </button>
                        <div className="flex items-center gap-1 flex-shrink-0 ml-2">
                          {!isDraft && s.status !== 'SIGNED' && (
                            <button onClick={() => handleResendEmail(s.id)} className="text-xs text-emerald-600 hover:text-emerald-800">Resend</button>
                          )}
                          {isDraft && (
                            <button onClick={() => handleRemoveSigner(s.id)} className="text-red-400 hover:text-red-600 text-xs">✕</button>
                          )}
                        </div>
                      </div>
                      {isExpanded && (
                        <div className="px-4 pb-3">
                          <p className="text-xs text-gray-500 mb-2">{s.email}</p>
                          {isDraft && (
                            <>
                              <div className="flex gap-1 flex-wrap mb-1">
                                {['SIGNATURE','INITIALS','DATE','NAME','EMAIL'].map(type => {
                                  const active = placingField?.signerId === s.id && placingField?.fieldType === type;
                                  return (
                                    <button key={type} onClick={() => setPlacingField(active ? null : {signerId: s.id, fieldType: type})}
                                      className={`px-2 py-1 rounded text-xs border ${active ? 'bg-emerald-600 text-white border-emerald-600' : 'border-gray-300 text-gray-700 hover:bg-gray-50'}`}>
                                      {{'SIGNATURE':'✍ Sign','INITIALS':'AB Init.','DATE':'📅 Date','NAME':'👤 Name','EMAIL':'✉ Email'}[type]}
                                    </button>
                                  );
                                })}
                              </div>
                              <div className="flex gap-1 flex-wrap mb-1">
                                {['FIRST_NAME','LAST_NAME','DATE_DAY','DATE_MONTH','DATE_YEAR_4','DATE_YEAR_2'].map(type => {
                                  const active = placingField?.signerId === s.id && placingField?.fieldType === type;
                                  return (
                                    <button key={type} onClick={() => setPlacingField(active ? null : {signerId: s.id, fieldType: type})}
                                      className={`px-2 py-1 rounded text-xs border ${active ? 'bg-emerald-600 text-white border-emerald-600' : 'border-gray-300 text-gray-700 hover:bg-gray-50'}`}>
                                      {{'FIRST_NAME':'First','LAST_NAME':'Last','DATE_DAY':'Day','DATE_MONTH':'Month','DATE_YEAR_4':'Year 4','DATE_YEAR_2':'Year 2'}[type]}
                                    </button>
                                  );
                                })}
                              </div>
                              <div className="flex gap-1 flex-wrap">
                                {['STREET_ADDRESS_1','STREET_ADDRESS_2','CITY','STATE','ZIP_CODE','COUNTRY','COUNTRY_ABBR'].map(type => {
                                  const active = placingField?.signerId === s.id && placingField?.fieldType === type;
                                  return (
                                    <button key={type} onClick={() => setPlacingField(active ? null : {signerId: s.id, fieldType: type})}
                                      className={`px-2 py-1 rounded text-xs border ${active ? 'bg-emerald-600 text-white border-emerald-600' : 'border-gray-300 text-gray-700 hover:bg-gray-50'}`}>
                                      {{'STREET_ADDRESS_1':'Addr 1','STREET_ADDRESS_2':'Addr 2','CITY':'City','STATE':'State','ZIP_CODE':'Zip','COUNTRY':'Country','COUNTRY_ABBR':'Ctry Abbr'}[type]}
                                    </button>
                                  );
                                })}
                                <button onClick={() => { const label = prompt('Label for this free-text field:'); if (label) setPlacingField({signerId: s.id, fieldType: 'FREE_TEXT', label}); }}
                                  className={`px-2 py-1 rounded text-xs border ${placingField?.signerId === s.id && placingField?.fieldType === 'FREE_TEXT' ? 'bg-emerald-600 text-white border-emerald-600' : 'border-gray-300 text-gray-700 hover:bg-gray-50'}`}>
                                  ✏ Free Text
                                </button>
                              </div>
                            </>
                          )}
                          {signerFields.length > 0 && (
                            <div className="mt-2 border-t pt-1 divide-y">
                              {signerFields.map(f => {
                                const isSelected = selectedField?.id === f.id;
                                const showSettings = isDraft && isSelected && !['SIGNATURE','INITIALS'].includes(f.fieldType);
                                return (
                                  <div key={f.id}>
                                    <div className={`py-1.5 cursor-pointer ${isSelected ? 'bg-emerald-50 -mx-4 px-4' : ''}`} onClick={() => {
                                      const next = isSelected ? null : f;
                                      setSelectedField(next);
                                      if (next && fieldOverlayRefs.current[f.id]) fieldOverlayRefs.current[f.id].scrollIntoView({behavior: 'smooth', block: 'center'});
                                    }}>
                                      <div className="flex items-center justify-between">
                                        <div className="text-xs">
                                          <span className="font-medium">{f.fieldType === 'FREE_TEXT' && f.label ? f.label : f.fieldType}</span>
                                          <span className="text-gray-400 ml-1">p{f.pageNumber}</span>
                                        </div>
                                        {isDraft && (
                                          <button onClick={(e) => { e.stopPropagation(); handleDeleteField(f.id); }} className="text-red-400 hover:text-red-600 text-xs">✕</button>
                                        )}
                                      </div>
                                    </div>
                                    {showSettings && (
                                      <div className="pb-2 bg-emerald-50 -mx-4 px-4 flex items-center gap-2 flex-wrap">
                                        <select value={selectedField.fontFamily || 'Helvetica'}
                                          onChange={e => { handleUpdateFieldStyle(selectedField, {fontFamily: e.target.value}); setSelectedField({...selectedField, fontFamily: e.target.value}); }}
                                          className="text-xs border border-gray-300 rounded px-1.5 py-1">
                                          <option value="Helvetica">Helvetica</option><option value="Times New Roman">Times New Roman</option><option value="Courier">Courier</option>
                                        </select>
                                        <select value={selectedField.fontSize || 14}
                                          onChange={e => { const v = parseInt(e.target.value); handleUpdateFieldStyle(selectedField, {fontSize: v}); setSelectedField({...selectedField, fontSize: v}); }}
                                          className="text-xs border border-gray-300 rounded px-1.5 py-1">
                                          {[8,9,10,11,12,14,16,18,20,24,28,32,36].map(sz => <option key={sz} value={sz}>{sz}pt</option>)}
                                        </select>
                                        <div className="flex border border-gray-300 rounded overflow-hidden">
                                          {['LEFT','CENTER','RIGHT'].map(a => (
                                            <button key={a} onClick={() => { handleUpdateFieldStyle(selectedField, {textAlign: a}); setSelectedField({...selectedField, textAlign: a}); }}
                                              className={`px-1.5 py-1 text-xs ${(selectedField.textAlign || 'LEFT') === a ? 'bg-emerald-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}>
                                              {a === 'LEFT' ? '⫷' : a === 'CENTER' ? '☰' : '⫸'}
                                            </button>
                                          ))}
                                        </div>
                                        {selectedField.fieldType === 'FREE_TEXT' && (
                                          <input type="text" value={selectedField.label || ''} placeholder="Field label"
                                            onChange={e => setSelectedField({...selectedField, label: e.target.value})}
                                            onBlur={e => handleUpdateFieldStyle(selectedField, {label: e.target.value})}
                                            className="text-xs border border-gray-300 rounded px-1.5 py-1 w-full mt-1" />
                                        )}
                                      </div>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
                {placingField && placingField.signerId && (
                  <div className="px-4 py-3 border-t">
                    <div className="bg-emerald-50 border border-emerald-200 rounded p-2 text-xs text-emerald-700">
                      Click on the document to place the field. <button onClick={() => setPlacingField(null)} className="underline ml-1">Cancel</button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* PDF Pages */}
        <div className="flex-1 space-y-4" data-scales={scalesReady}>
          {pages.length === 0 && <div className="text-center py-12 text-gray-400">Loading PDF...</div>}
          {pages.map(({pageNum}) => {
            const scaleInfo = pdfScales.current[pageNum];
            const pageFields = doc.fields?.filter(f => f.pageNumber === pageNum) || [];
            return (
              <div key={pageNum} className="relative bg-white rounded-lg shadow">
                <div className="text-xs text-gray-400 px-3 py-1 border-b">Page {pageNum}</div>
                <div className="relative"
                  onClick={(e) => handlePageClick(e, pageNum)}
                  onMouseMove={(e) => { if (draggingField) { const rect = e.currentTarget.getBoundingClientRect(); setDragPos({x: e.clientX - rect.left, y: e.clientY - rect.top, pageNum}); } }}
                  onMouseUp={(e) => { setDragPos(null); handlePageMouseUp(e, pageNum); }}
                  style={{cursor: placingField ? 'crosshair' : draggingField ? 'grabbing' : 'default'}}>
                  <canvas ref={el => { pageRefs.current[pageNum] = el; }} className="w-full block" />
                  {/* Render field overlays */}
                  {scaleInfo && pageFields.map(f => {
                    const rf = resizingField?.id === f.id ? resizingField : f;
                    const left = f.xPosition * scaleInfo.scale;
                    const top = f.yPosition * scaleInfo.scale;
                    const w = rf.width * scaleInfo.scale;
                    const h = rf.height * scaleInfo.scale;
                    const color = isDocField(f.fieldType) ? DOC_FIELD_COLOR : signerColor(doc.signers, f.signerId);
                    const signer = doc.signers?.find(s => s.id === f.signerId);
                    const isTextField = !['SIGNATURE','INITIALS'].includes(f.fieldType);
                    const placeholderText = isTextField ? (isDocField(f.fieldType) ? (f.value || DOC_FIELD_PLACEHOLDERS[f.fieldType] || f.fieldType) : ({'DATE':'01/01/2025','NAME': signer?.name || 'Full Name','EMAIL': signer?.email || 'email@example.com','STREET_ADDRESS_1':'123 Main St','STREET_ADDRESS_2':'Apt 4B','CITY':'City','STATE':'ST','ZIP_CODE':'12345','COUNTRY':'United States','COUNTRY_ABBR':'US','FIRST_NAME': signer?.name?.split(' ')[0] || 'First','LAST_NAME': signer?.name?.split(' ').slice(1).join(' ') || 'Last','DATE_DAY': new Date().getDate().toString(),'DATE_MONTH': new Date().toLocaleString('default',{month:'long'}),'DATE_YEAR_4': new Date().getFullYear().toString(),'DATE_YEAR_2': new Date().getFullYear().toString().slice(-2),'FREE_TEXT': f.label || 'Free text'}[f.fieldType] || f.fieldType)) : null;
                    const fontSize = isTextField ? (f.fontSize || 14) * scaleInfo.scale : undefined;
                    const fontFamily = isTextField ? (f.fontFamily || 'Helvetica') : undefined;
                    const textAlign = isTextField ? (f.textAlign || 'LEFT').toLowerCase() : undefined;
                    const isSelected = selectedField?.id === f.id;
                    return (
                      <div key={f.id}
                        ref={el => { fieldOverlayRefs.current[f.id] = el; }}
                        onMouseDown={(e) => handleFieldMouseDown(e, f)}
                        style={{
                          position: 'absolute', left, top, width: w, height: h,
                          border: isSelected ? '3px solid #000' : `2px solid ${color}`,
                          backgroundColor: isSelected ? `${color}40` : `${color}20`,
                          cursor: isDraft ? 'grab' : 'default',
                          opacity: draggingField?.id === f.id ? 0.3 : 1,
                          zIndex: isSelected ? 10 : 1,
                          boxShadow: isSelected ? '0 0 0 2px rgba(0,0,0,0.3)' : 'none',
                        }}
                        className="rounded select-none group">
                        {isTextField ? (
                          <span className="absolute bottom-0 left-0 right-0 truncate px-1" style={{color, fontSize, fontFamily, textAlign, lineHeight: 1.2}}>
                            {placeholderText}
                          </span>
                        ) : (
                          <span className="absolute inset-0 flex items-center justify-center text-xs font-medium truncate px-1" style={{color}}>
                            {f.fieldType === 'SIGNATURE' ? '✍' : 'AB'}
                            {signer ? ` ${signer.name.split(' ')[0]}` : ''}
                          </span>
                        )}
                        {isDraft && (
                          <button onClick={(e) => { e.stopPropagation(); handleDeleteField(f.id); }}
                            className="absolute -top-2 -right-2 w-4 h-4 bg-red-500 text-white rounded-full text-xs leading-none hidden group-hover:flex items-center justify-center">×</button>
                        )}
                        {isDraft && (
                          <div onMouseDown={(e) => handleResizeMouseDown(e, f, pageNum)}
                            className="absolute bottom-0 right-0 w-3 h-3 cursor-se-resize hidden group-hover:block"
                            style={{backgroundColor: color, borderRadius: '0 0 4px 0'}} />
                        )}
                      </div>
                    );
                  })}
                  {/* Drag ghost outline */}
                  {draggingField && dragPos && dragPos.pageNum === pageNum && (
                    <div style={{
                      position: 'absolute',
                      left: dragPos.x - dragOffset.x,
                      top: dragPos.y - dragOffset.y,
                      width: draggingField.width * (pdfScales.current[pageNum]?.scale || 1),
                      height: draggingField.height * (pdfScales.current[pageNum]?.scale || 1),
                      border: '2px dashed #059669',
                      backgroundColor: 'rgba(5, 150, 105, 0.1)',
                      borderRadius: 4,
                      pointerEvents: 'none',
                    }} />
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Add Signer Modal */}
      {showAddSigner && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setShowAddSigner(false)}>
          <form onSubmit={handleAddSigner} onClick={e => e.stopPropagation()} className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md mx-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Add Signer</h3>
            <div className="space-y-3">
              <input autoFocus type="text" value={signerName} onChange={e => setSignerName(e.target.value)}
                placeholder="Full name" className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 outline-none" />
              <input type="email" value={signerEmail} onChange={e => setSignerEmail(e.target.value)}
                placeholder="Email address" className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 outline-none" />
            </div>
            <div className="flex justify-end gap-3 mt-4">
              <button type="button" onClick={() => setShowAddSigner(false)} className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg">Cancel</button>
              <button type="submit" disabled={!signerEmail.trim() || !signerName.trim()} className="px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 disabled:opacity-50">Add</button>
            </div>
          </form>
        </div>
      )}
      {/* Send Confirmation Modal */}
      {showSendConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setShowSendConfirm(false)}>
          <div onClick={e => e.stopPropagation()} className="bg-white rounded-lg shadow-xl w-full max-w-lg mx-4 max-h-[80vh] overflow-y-auto">
            <div className="p-6 border-b">
              <h3 className="text-lg font-semibold text-gray-900">Confirm & Send for Signing</h3>
              <p className="text-sm text-gray-500 mt-1">Review the details below before sending.</p>
            </div>
            <div className="p-6 space-y-4">
              {/* Document Info */}
              <div>
                <div className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">Document</div>
                <div className="text-sm font-medium text-gray-900">{doc.title}</div>
                <div className="text-xs text-gray-500">{doc.fileName}</div>
              </div>

              {/* Document Fields */}
              {doc.fields?.filter(f => isDocField(f.fieldType)).length > 0 && (
                <div>
                  <div className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">Document Fields</div>
                  <div className="space-y-1.5">
                    {doc.fields.filter(f => isDocField(f.fieldType)).map(f => (
                      <div key={f.id} className="flex items-center justify-between bg-gray-50 rounded px-3 py-2">
                        <span className="text-xs font-medium text-gray-600">{DOC_FIELD_LABELS[f.fieldType] || f.fieldType}</span>
                        <span className={`text-sm ${f.value ? 'text-gray-900 font-medium' : 'text-red-500 italic'}`}>
                          {f.value || 'Not set'}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Signers */}
              <div>
                <div className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">Signers</div>
                <div className="space-y-1.5">
                  {doc.signers?.map((s, i) => {
                    const signerFields = doc.fields?.filter(f => f.signerId === s.id) || [];
                    return (
                      <div key={s.id} className="bg-gray-50 rounded px-3 py-2">
                        <div className="flex items-center gap-2">
                          <div className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{backgroundColor: SIGNER_COLORS[i % SIGNER_COLORS.length]}} />
                          <span className="text-sm font-medium text-gray-900">{s.name}</span>
                          <span className="text-xs text-gray-500">{s.email}</span>
                        </div>
                        {signerFields.length > 0 && (
                          <div className="mt-1 ml-4.5 text-xs text-gray-500">
                            {signerFields.map(f => f.fieldType === 'FREE_TEXT' && f.label ? f.label : f.fieldType).join(', ')}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Warnings */}
              {doc.fields?.some(f => isDocField(f.fieldType) && !f.value) && (
                <div className="bg-amber-50 border border-amber-200 rounded p-3 text-xs text-amber-700">
                  ⚠ Some document fields have no value set. They will appear blank on the signed document.
                </div>
              )}
            </div>
            <div className="flex justify-end gap-3 p-6 border-t bg-gray-50 rounded-b-lg">
              <button onClick={() => setShowSendConfirm(false)} className="px-4 py-2 text-gray-700 hover:bg-gray-200 rounded-lg text-sm">Cancel</button>
              <button onClick={handleSend} className="px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 text-sm">Send Now</button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}
